// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState, useCallback, type FocusEvent, type KeyboardEvent } from 'react';

type Options = {
  value?: number | null;
  onChange: (value: number | null) => void;
  onSubmit?: () => void;
  defaultValue?: string | number;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
};

/** Mantine's `NumberInput` reports in-progress input like `0.0`, `0009`, or `1.` as a string rather than a number. */
const parseInputValue = (value: string | number) => {
  if (typeof value === 'number') return value;
  return value === '' ? null : Number(value);
};

export const useUncontrolledNumber = ({
  onSubmit,
  value: parentValue,
  defaultValue,
  onChange: parentOnChange,
  onBlur: parentOnBlur,
  onKeyDown: parentOnKeyDown,
}: Options) => {
  const [internalValue, setInternalValue] = useState<string | number>(parentValue ?? defaultValue ?? '');
  useEffect(() => {
    // Keep in-progress input like `0.0` or `0009` rather than replacing it with its equivalent number.
    setInternalValue((current) =>
      parentValue === undefined ? current : parseInputValue(current) === parentValue ? current : (parentValue ?? ''),
    );
  }, [parentValue]);
  const onBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      setInternalValue((v) => parentValue ?? v);
      parentOnBlur?.(e);
    },
    [parentValue, parentOnBlur],
  );
  const onChange = useCallback(
    (v: string | number) => {
      setInternalValue(v);
      // Unparseable input like `-` or `.` stays local until it becomes a number.
      const parsed = parseInputValue(v);
      if (!Number.isNaN(parsed)) parentOnChange(parsed);
    },
    [parentOnChange],
  );
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') onSubmit?.();
      parentOnKeyDown?.(e);
    },
    [parentOnKeyDown, onSubmit],
  );
  return {
    value: internalValue,
    defaultValue,
    onChange,
    onBlur,
    onKeyDown,
  };
};
